/**
 * Turning a half-typed form into line items.
 *
 * Draft rows hold STRINGS because that is what a form holds: "12." and "" are legitimate
 * states while someone is typing, and coercing on every keystroke fights the person doing
 * the typing. Conversion happens ONCE, here, at submit — and it lives in shared rather
 * than in the form component because it is a money rule, and money rules get tested.
 */
import type { LineItemKind } from './schemas/money';
import { computeLineTotal } from './money';

export interface LineDraft {
  name: string;
  description?: string;
  taxable?: boolean;
  qty: string;
  price: string;
  kind: LineItemKind;
  /**
   * The price-book item this row was picked from, if it was (Inventory v2 Phase 1): what lets
   * a part used on the job be matched to the line. Cleared the moment the name stops matching.
   */
  priceBookItemId?: string;
  sourceLocationId?: string;
  assignedUserId?: string;
  taskName?: string;
  unitLabel?: string;
  estimatedDurationMinutes?: number;
  requestedStartAt?: string;
  requestedEndAt?: string;
}

export const emptyLine = (): LineDraft => ({
  name: '',
  qty: '1',
  price: '',
  kind: 'service',
});

export interface ParsedLine {
  priceBookItemId?: string;
  sourceLocationId?: string;
  assignedUserId?: string;
  taskName?: string;
  unitLabel?: string;
  estimatedDurationMinutes?: number;
  requestedStartAt?: Date;
  requestedEndAt?: Date;
  kind: LineItemKind;
  name: string;
  description?: string;
  qty: number;
  unitPriceCents: number;
  taxable: boolean;
}

/** Dollars as typed → integer cents. "12.345" rounds; junk becomes null, never 0. */
export function priceToCents(price: string): number | null {
  const trimmed = price.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/**
 * Only rows a human actually filled in. A blank row someone tabbed past is not a line
 * item, and a row with a name but no price is an unfinished thought — including either
 * as a $0 line silently changes what the customer is quoted.
 */
export function parseLines(lines: LineDraft[]): ParsedLine[] {
  const out: ParsedLine[] = [];
  for (const l of lines) {
    const name = l.name.trim();
    const unitPriceCents = priceToCents(l.price);
    if (!name || unitPriceCents === null) continue;
    const qty = Number(l.qty);
    out.push({
      ...(l.priceBookItemId ? { priceBookItemId: l.priceBookItemId } : {}),
      ...(l.sourceLocationId ? { sourceLocationId: l.sourceLocationId } : {}),
      ...(l.assignedUserId ? { assignedUserId: l.assignedUserId } : {}),
      ...(l.taskName ? { taskName: l.taskName } : {}),
      ...(l.unitLabel ? { unitLabel: l.unitLabel } : {}),
      ...(l.estimatedDurationMinutes !== undefined
        ? { estimatedDurationMinutes: l.estimatedDurationMinutes }
        : {}),
      ...(l.requestedStartAt ? { requestedStartAt: new Date(l.requestedStartAt) } : {}),
      ...(l.requestedEndAt ? { requestedEndAt: new Date(l.requestedEndAt) } : {}),
      kind: l.kind,
      name,
      ...(l.description !== undefined ? { description: l.description } : {}),
      qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
      unitPriceCents,
      taxable: l.taxable ?? true,
    });
  }
  return out;
}

/** Strict quote intent: incomplete or invalid quantities stay invalid, never become one. */
export function parseQuoteLines(lines: LineDraft[]): ParsedLine[] {
  return parseLines(
    lines.filter((line) => {
      const typed = line.qty.trim();
      const qty = Number(typed);
      const start = line.requestedStartAt,
        end = line.requestedEndAt;
      const windowValid =
        (!start && !end) ||
        Boolean(
          start &&
            end &&
            Number.isFinite(Date.parse(start)) &&
            Number.isFinite(Date.parse(end)) &&
            Date.parse(start) < Date.parse(end) &&
            (line.kind === 'service' || line.kind === 'labor'),
        );
      return typed !== '' && Number.isFinite(qty) && qty > 0 && windowValid;
    }),
  );
}

/** Pre-tax total of the complete rows, using the same rounding as a saved line. */
export function linesTotalCents(lines: LineDraft[]): number {
  return parseLines(lines).reduce(
    (sum, l) => sum + computeLineTotal(l.qty, l.unitPriceCents),
    0,
  );
}
